import { supabase } from '../lib/supabase';

/**
 * Student Progress Service for managing student learning trail progress
 */

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj) return obj;
  
  const camelObj = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  });
  return camelObj;
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj) return obj;
  
  const snakeObj = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = obj?.[key];
  });
  return snakeObj;
}

/**
 * Get student progress for all trails in their classroom
 * @param {string} studentId - The student's ID
 * @param {string} classroomId - The classroom ID
 * @returns {Promise<{progress: Array, error: null} | {progress: null, error: string}>}
 */
export const getStudentProgress = async (studentId, classroomId) => {
  try {
    if (!classroomId) {
      console.warn('getStudentProgress: classroomId is missing, cannot fetch trails');
      return { progress: [], error: null };
    }

    // Query trails assigned to this classroom via junction table
    const { data: trailClassrooms, error: tcError } = await supabase
      ?.from('trail_classrooms')
      ?.select('trail_id')
      ?.eq('classroom_id', classroomId);

    if (tcError) {
      console.error('trail_classrooms query error:', tcError);
    }

    // Fallback: also get trails with direct classroom_id (backward compat)
    const { data: directTrails, error: dtError } = await supabase
      ?.from('learning_trails')
      ?.select('id')
      ?.eq('classroom_id', classroomId);

    if (dtError) {
      console.error('direct trails query error:', dtError);
    }

    if (tcError && dtError) {
      console.error('Get student progress error:', tcError);
      return { progress: null, error: tcError?.message };
    }

    // Combine trail IDs from both sources (deduplicated)
    const junctionTrailIds = trailClassrooms?.map(tc => tc?.trail_id) || [];
    const directTrailIds = directTrails?.map(t => t?.id) || [];
    const allTrailIds = [...new Set([...junctionTrailIds, ...directTrailIds])];

    console.log('Student trails lookup - classroomId:', classroomId, 'junctionIds:', junctionTrailIds, 'directIds:', directTrailIds, 'allIds:', allTrailIds);

    if (allTrailIds?.length === 0) {
      return { progress: [], error: null };
    }

    const { data, error } = await supabase?.from('learning_trails')?.select(`
        *,
        student_progress!left(
          id,
          student_id,
          started_at,
          completed_at,
          completion_percentage,
          answers_data,
          last_accessed_at
        )
      `)?.in('id', allTrailIds)?.eq('student_progress.student_id', studentId)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Get student progress error:', error);
      if (error?.message?.includes('student_progress') && error?.message?.includes('schema cache')) {
        return { progress: null, error: 'Database setup incomplete. Please contact your teacher.' };
      }
      return { progress: null, error: error?.message };
    }

    const transformedData = data?.map(trail => ({
      ...toCamelCase(trail),
      studentProgress: trail?.student_progress?.[0] ? toCamelCase(trail?.student_progress?.[0]) : null
    }));

    return { progress: transformedData, error: null };
  } catch (error) {
    console.error('Get student progress exception:', error);
    return { progress: null, error: 'Failed to fetch student progress' };
  }
};

/**
 * Start a trail for a student
 * @param {string} studentId - The student's ID
 * @param {string} trailId - The trail's ID
 * @returns {Promise<{progress: Object, error: null} | {progress: null, error: string}>}
 */
export const startTrail = async (studentId, trailId) => {
  try {
    const { data, error } = await supabase?.from('student_progress')?.upsert([{
        student_id: studentId,
        trail_id: trailId,
        started_at: new Date()?.toISOString(),
        completion_percentage: 0,
        answers_data: {},
        last_accessed_at: new Date()?.toISOString()
      }], { onConflict: 'student_id,trail_id' })?.select()?.single();

    if (error) {
      // PGRST116 means 0 rows returned - row already exists, fetch it
      if (error?.code === 'PGRST116') {
        const { data: existingData, error: fetchError } = await supabase
          ?.from('student_progress')
          ?.select()
          ?.eq('student_id', studentId)
          ?.eq('trail_id', trailId)
          ?.single();
        if (fetchError) {
          console.error('Fetch existing trail progress error:', fetchError);
          return { progress: null, error: fetchError?.message };
        }
        return { progress: existingData ? toCamelCase(existingData) : null, error: null };
      }
      console.error('Start trail error:', error);
      return { progress: null, error: error?.message };
    }

    return { progress: data ? toCamelCase(data) : null, error: null };
  } catch (error) {
    console.error('Start trail exception:', error);
    return { progress: null, error: 'Failed to start trail' };
  }
};

/**
 * Update student progress for a trail
 * @param {string} studentId - The student's ID
 * @param {string} trailId - The trail's ID
 * @param {Object} updates - Progress updates
 * @returns {Promise<{progress: Object, error: null} | {progress: null, error: string}>}
 */
export const updateProgress = async (studentId, trailId, updates) => {
  try {
    const updateData = {};
    if (updates?.completionPercentage !== undefined) updateData.completion_percentage = updates?.completionPercentage;
    if (updates?.answersData !== undefined) updateData.answers_data = updates?.answersData;
    if (updates?.completedAt !== undefined) updateData.completed_at = updates?.completedAt;
    updateData.last_accessed_at = new Date()?.toISOString();

    const { data, error } = await supabase?.from('student_progress')?.update(updateData)?.eq('student_id', studentId)?.eq('trail_id', trailId)?.select()?.single();

    if (error) {
      console.error('Update progress error:', error);
      return { progress: null, error: error?.message };
    }

    return { progress: toCamelCase(data), error: null };
  } catch (error) {
    console.error('Update progress exception:', error);
    return { progress: null, error: 'Failed to update progress' };
  }
};

/**
 * Update content completion status for a specific content block
 * @param {string} studentId - The student's ID
 * @param {string} trailId - The trail's ID
 * @param {string} contentId - The content block's ID
 * @param {Object} completionData - Completion data (completed, completedAt, attempts)
 * @returns {Promise<{success: boolean, error: null} | {success: false, error: string}>}
 */
export const updateContentCompletion = async (studentId, trailId, contentId, completionData) => {
  try {
    // First, get current progress to merge with existing content_completion_status
    const { data: currentProgress, error: fetchError } = await supabase
      ?.from('student_progress')
      ?.select('content_completion_status')
      ?.eq('student_id', studentId)
      ?.eq('trail_id', trailId)
      ?.single();

    if (fetchError) {
      // PGRST116 means no progress row yet - start the trail first
      if (fetchError?.code === 'PGRST116') {
        await startTrail(studentId, trailId);
      } else {
        console.error('Fetch current progress error:', fetchError);
        return { success: false, error: fetchError?.message };
      }
    }

    // Merge new completion data with existing
    const updatedCompletionStatus = {
      ...(currentProgress?.content_completion_status || {}),
      [contentId]: {
        completed: completionData?.completed || false,
        completedAt: completionData?.completedAt || new Date()?.toISOString(),
        attempts: completionData?.attempts || 1
      }
    };

    // Update the progress record
    const { error: updateError } = await supabase
      ?.from('student_progress')
      ?.update({
        content_completion_status: updatedCompletionStatus,
        last_accessed_at: new Date()?.toISOString()
      })
      ?.eq('student_id', studentId)
      ?.eq('trail_id', trailId);

    if (updateError) {
      console.error('Update content completion error:', updateError);
      return { success: false, error: updateError?.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Update content completion exception:', error);
    return { success: false, error: 'Failed to update content completion' };
  }
};

/**
 * Get content completion status for a trail
 * @param {string} studentId - The student's ID
 * @param {string} trailId - The trail's ID
 * @returns {Promise<{completionStatus: Object, error: null} | {completionStatus: null, error: string}>}
 */
export const getContentCompletionStatus = async (studentId, trailId) => {
  try {
    const { data, error } = await supabase
      ?.from('student_progress')
      ?.select('content_completion_status')
      ?.eq('student_id', studentId)
      ?.eq('trail_id', trailId)
      ?.single();

    if (error) {
      // PGRST116 means no progress row yet - return empty status
      if (error?.code === 'PGRST116') {
        return { completionStatus: {}, error: null };
      }
      console.error('Get content completion status error:', error);
      return { completionStatus: null, error: error?.message };
    }

    return { completionStatus: data?.content_completion_status || {}, error: null };
  } catch (error) {
    console.error('Get content completion status exception:', error);
    return { completionStatus: null, error: 'Failed to fetch content completion status' };
  }
};

/**
 * Get learning activity history for a student
 * @param {string} studentId - The student's ID
 * @returns {Promise<{activities: Array, error: null} | {activities: null, error: string}>}
 */
export const getActivityHistory = async (studentId) => {
  try {
    const { data, error } = await supabase?.from('student_progress')?.select(`
        id,
        student_id,
        trail_id,
        started_at,
        completed_at,
        completion_percentage,
        answers_data,
        last_accessed_at,
        created_at,
        updated_at
      `)?.eq('student_id', studentId)?.order('last_accessed_at', { ascending: false })?.limit(10);

    if (error) {
      console.error('Get activity history error:', error);
      // Check if it's a missing table error
      if (error?.message?.includes('student_progress') && error?.message?.includes('schema cache')) {
        return { activities: [], error: null }; // Return empty array instead of error
      }
      return { activities: null, error: error?.message };
    }

    // Fetch trail details separately for each activity
    const activitiesWithTrails = await Promise.all(
      data?.map(async (activity) => {
        const { data: trailData } = await supabase
          ?.from('learning_trails')
          ?.select('title, description, image_url')
          ?.eq('id', activity?.trail_id)
          ?.single();

        return {
          ...toCamelCase(activity),
          learningTrails: trailData ? toCamelCase(trailData) : null
        };
      }) || []
    );

    return { activities: activitiesWithTrails, error: null };
  } catch (error) {
    console.error('Get activity history exception:', error);
    return { activities: [], error: null }; // Return empty array for graceful degradation
  }
};

/**
 * Calculate performance insights for a student
 * @param {string} studentId - The student's ID
 * @returns {Promise<{insights: Object, error: null} | {insights: null, error: string}>}
 */
export const getPerformanceInsights = async (studentId) => {
  try {
    const { data, error } = await supabase?.from('student_progress')?.select('*')?.eq('student_id', studentId);

    if (error) {
      console.error('Get performance insights error:', error);
      // Check if it's a missing table error - return default insights
      if (error?.message?.includes('student_progress') && error?.message?.includes('schema cache')) {
        return { 
          insights: {
            totalTrails: 0,
            completedTrails: 0,
            inProgressTrails: 0,
            notStartedTrails: 0,
            avgQuizScore: 0,
            learningStreak: 0,
            totalQuizzes: 0,
            totalCorrect: 0
          }, 
          error: null 
        };
      }
      return { insights: null, error: error?.message };
    }

    // Calculate insights
    const totalTrails = data?.length || 0;
    const completedTrails = data?.filter(p => p?.completed_at)?.length || 0;
    const inProgressTrails = data?.filter(p => !p?.completed_at && p?.completion_percentage > 0)?.length || 0;
    
    // Calculate average quiz score
    let totalQuizzes = 0;
    let totalCorrect = 0;
    data?.forEach(progress => {
      if (progress?.answers_data?.quiz_scores) {
        const quizScores = progress?.answers_data?.quiz_scores;
        totalQuizzes += quizScores?.length || 0;
        totalCorrect += quizScores?.filter(q => q?.correct)?.length || 0;
      }
    });
    const avgQuizScore = totalQuizzes > 0 ? Math.round((totalCorrect / totalQuizzes) * 100) : 0;

    // Calculate learning streak (days with activity in last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
    const recentActivity = data?.filter(p => new Date(p?.last_accessed_at) > sevenDaysAgo);
    const uniqueDays = new Set(recentActivity?.map(p => new Date(p?.last_accessed_at)?.toDateString()));
    const learningStreak = uniqueDays?.size || 0;

    const insights = {
      totalTrails,
      completedTrails,
      inProgressTrails,
      notStartedTrails: 0, // Will be calculated in component
      avgQuizScore,
      learningStreak,
      totalQuizzes,
      totalCorrect
    };

    return { insights, error: null };
  } catch (error) {
    console.error('Get performance insights exception:', error);
    return { insights: null, error: 'Failed to calculate performance insights' };
  }
};

/**
 * Get comprehensive performance analytics for a student
 * @param {string} studentId - The student's ID
 * @returns {Promise<{analytics: Object, error: null} | {analytics: null, error: string}>}
 */
export const getPerformanceAnalytics = async (studentId) => {
  try {
    const { data, error } = await supabase
      ?.from('student_progress')
      ?.select(`
        id,
        trail_id,
        started_at,
        completed_at,
        completion_percentage,
        answers_data,
        last_accessed_at
      `)
      ?.eq('student_id', studentId)
      ?.order('last_accessed_at', { ascending: false });

    if (error) {
      console.error('Get performance analytics error:', error);
      return { analytics: getDefaultAnalytics(), error: null };
    }

    if (!data || data?.length === 0) {
      return { analytics: getDefaultAnalytics(), error: null };
    }

    // Fetch trail titles for each progress record
    const trailIds = [...new Set(data.map(p => p.trail_id))];
    const { data: trailsData } = await supabase
      ?.from('learning_trails')
      ?.select('id, title')
      ?.in('id', trailIds);

    const trailMap = {};
    trailsData?.forEach(t => { trailMap[t.id] = t?.title; });

    // Compute stats
    const completedTrails = data?.filter(p => p?.completed_at)?.length;
    const inProgressTrails = data?.filter(p => !p?.completed_at && p?.completion_percentage > 0)?.length;

    // Quiz stats
    let totalQuizzes = 0;
    let totalCorrect = 0;
    const quizPerformanceByTrail = [];

    data?.forEach(progress => {
      const quizScores = progress?.answers_data?.quiz_scores;
      if (quizScores && quizScores?.length > 0) {
        const correct = quizScores?.filter(q => q?.correct)?.length;
        totalQuizzes += quizScores?.length;
        totalCorrect += correct;
        const score = Math.round((correct / quizScores?.length) * 100);
        quizPerformanceByTrail?.push({
          trailName: (trailMap?.[progress?.trail_id] || 'Trail')?.substring(0, 12),
          score
        });
      }
    });

    const avgQuizScore = totalQuizzes > 0 ? Math.round((totalCorrect / totalQuizzes) * 100) : 0;

    // Learning streak (unique active days in last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
    const recentActivity = data?.filter(p => new Date(p.last_accessed_at) > sevenDaysAgo);
    const uniqueDays = new Set(recentActivity.map(p => new Date(p.last_accessed_at).toDateString()));
    const learningStreak = uniqueDays?.size;

    // Progress over time (last 7 days)
    const progressOverTime = buildProgressOverTime(data);

    // Recent activity with time ago
    const recentActivityList = data?.slice(0, 6)?.map(p => ({
      trailTitle: trailMap?.[p?.trail_id] || 'Learning Trail',
      completedAt: p?.completed_at,
      completionPercentage: p?.completion_percentage,
      timeAgo: getTimeAgo(p?.last_accessed_at)
    }));

    return {
      analytics: {
        completedTrails,
        inProgressTrails,
        avgQuizScore,
        totalQuizzes,
        totalCorrect,
        learningStreak,
        progressOverTime,
        quizPerformanceByTrail,
        recentActivity: recentActivityList
      },
      error: null
    };
  } catch (error) {
    console.error('Get performance analytics exception:', error);
    return { analytics: getDefaultAnalytics(), error: null };
  }
};

function getDefaultAnalytics() {
  return {
    completedTrails: 0,
    inProgressTrails: 0,
    avgQuizScore: 0,
    totalQuizzes: 0,
    totalCorrect: 0,
    learningStreak: 0,
    progressOverTime: [],
    quizPerformanceByTrail: [],
    recentActivity: []
  };
}

function buildProgressOverTime(data) {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d?.setDate(d?.getDate() - i);
    const dateStr = d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dayEnd = new Date(dayStart.getTime() + 86400000);

    const dayRecords = data?.filter(p => {
      const accessed = new Date(p.last_accessed_at);
      return accessed >= dayStart && accessed < dayEnd;
    });

    const avgCompletion = dayRecords?.length > 0
      ? Math.round(dayRecords?.reduce((sum, p) => sum + (p?.completion_percentage || 0), 0) / dayRecords?.length)
      : null;

    days?.push({ date: dateStr, avgCompletion: avgCompletion ?? 0 });
  }
  return days;
}

function getTimeAgo(timestamp) {
  if (!timestamp) return '';
  const now = new Date();
  const t = new Date(timestamp);
  const diffMs = now - t;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return t?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}